"use client"

import { Skeleton, cn } from "@rarible_int/anaconda_ui_components"
import { action, atom } from "@reatom/core"
import { molecule } from "bunshi"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { DesktopSlidePreview } from "./desktop-slide-preview"
import { MobileSlidePreview } from "./mobile-slide-preview"
import { Slide, SlideSkeleton } from "./slide"
import type { SlideType } from "./types"

/**
 * Molecule for managing slider state and controlling data fetching.
 * This is the central controller for all slide-related business logic.
 */
export const SliderMolecule = molecule(() => {
  const activeSlideIndexAtom = atom(0, "activeSlideIndexAtom")

  const setActiveSlideIndex = action((index: number) => {
    activeSlideIndexAtom.set(index)
  }, "setActiveSlideIndex")

  return {
    activeSlideIndexAtom,
    setActiveSlideIndex,
  }
})
interface Props {
  slides: SlideType[]
  slideDurationInSeconds?: number
  className?: string
  containerClassName?: string
  onSlideChange?: (index: number) => void
}

export function Slider({
  slides,
  slideDurationInSeconds = 5,
  className,
  containerClassName,
  onSlideChange,
}: Props) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [sliderActive, setSliderActive] = useState(false)
  const router = useRouter()

  // Start the slider on mount during client-side hydration
  useEffect(() => setSliderActive(true), [])

  useEffect(() => {
    for (const slide of slides) {
      if (slide.link) {
        router.prefetch(slide.link)
      }
    }
  }, [slides, router])

  // Notify parent of slide changes
  useEffect(() => {
    onSlideChange?.(activeSlideIndex)
  }, [activeSlideIndex, onSlideChange])

  useEffect(() => {
    if (!sliderActive) return

    const timeout = setTimeout(() => {
      setActiveSlideIndex((activeSlideIndex + 1) % slides.length)
    }, slideDurationInSeconds * 1000)

    return () => clearTimeout(timeout)
  }, [slideDurationInSeconds, activeSlideIndex, slides.length, sliderActive])

  if (slides.length === 0) return null

  return (
    <SliderContainer className={className}>
      <div
        className="flex transition-transform duration-500"
        style={{
          transform: `translateX(-${activeSlideIndex * 100}%)`,
        }}
      >
        {slides.map((slide, index) => (
          <Slide
            key={`${slide.name}|${slide.background}`}
            slide={slide}
            containerClassName={containerClassName}
            active={index === activeSlideIndex}
            preload={
              index === activeSlideIndex ||
              index === (activeSlideIndex + 1) % slides.length
            }
          />
        ))}
      </div>
      <div className="absolute right-0 bottom-0 hidden h-[168px] items-end gap-4 p-4 lg:flex lg:p-6">
        {slides.map((slide, index) => (
          <DesktopSlidePreview
            timerDurationInSeconds={slideDurationInSeconds}
            onClick={() => {
              const active = index === activeSlideIndex

              if (active && slide.link) {
                router.push(slide.link)
                return
              }

              setActiveSlideIndex(index)
            }}
            key={`${slide.name}|${slide.background}`}
            slideActive={index === activeSlideIndex}
            withTimer={slides.length > 1 && sliderActive}
            mediaSrc={slide.preview ?? slide.background}
            mediaType={slide.previewType}
            posterSrc={slide.previewPoster}
            mediaSequence={slide.previewImages}
            title={slide.name}
          />
        ))}
      </div>
      <div className="flex items-end justify-center gap-2 lg:hidden">
        {slides.map((slide, index) => (
          <MobileSlidePreview
            timerDurationInSeconds={slideDurationInSeconds}
            onClick={() => setActiveSlideIndex(index)}
            key={`${slide.name}|${slide.background}`}
            slideActive={index === activeSlideIndex}
            withTimer={sliderActive}
          />
        ))}
      </div>
    </SliderContainer>
  )
}

export function SliderSkeleton({
  containerClassName,
}: { containerClassName?: string }) {
  return (
    <SliderContainer>
      <SlideSkeleton containerClassName={containerClassName} />
      <div className="h-1 w-full lg:hidden" />
    </SliderContainer>
  )
}

function SliderContainer({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-4 overflow-hidden lg:rounded-xl lg:border lg:border-foreground/10",
        className,
      )}
    >
      {children}
    </div>
  )
}
